const text = (value, maximum = 500, minimum = 1) =>
  typeof value === 'string' &&
  value.trim().length >= minimum &&
  value.length <= maximum &&
  Array.from(value).every(character => {
    const code = character.charCodeAt(0);
    return code >= 32 && (code < 127 || code > 159);
  });
const numeric = value => typeof value === 'number' && Number.isFinite(value);
const scalar = value =>
  numeric(value) || typeof value === 'boolean' || text(value);
const listOperators = [
  'in',
  'not_in',
  'contains_any',
  'contains_all',
  'contains_none',
];
const textOperators = ['text_contains_any', 'text_not_contains_any'];
// Persisted v1 facet predicates have a different compilation cost from typed facts.
const legacy = ({ field, operator }) =>
  (['category_ids', 'brand_id'].includes(field) &&
    ['in', 'contains_any'].includes(operator)) ||
  (field === 'price' && ['gte', 'lte', 'between'].includes(operator)) ||
  (field.startsWith('attributes.') &&
    ['in', 'contains_any', 'gte', 'lte', 'between'].includes(operator));
const leafValid = item => {
  if (!item || typeof item.field !== 'string' || !item.field) return false;
  if (item.operator === 'exists') return typeof item.value === 'boolean';
  if (textOperators.includes(item.operator))
    return (
      Array.isArray(item.value) &&
      item.value.length >= 1 &&
      item.value.length <= 10 &&
      item.value.every(value => text(value, 100, 3))
    );
  if (listOperators.includes(item.operator))
    return (
      Array.isArray(item.value) &&
      item.value.length >= 1 &&
      item.value.length <= 100 &&
      item.value.every(scalar)
    );
  if (item.operator === 'between')
    return (
      Array.isArray(item.value) &&
      item.value.length === 2 &&
      item.value.every(numeric) &&
      item.value[0] <= item.value[1]
    );
  if (['gt', 'gte', 'lt', 'lte'].includes(item.operator))
    return numeric(item.value);
  return ['eq', 'neq'].includes(item.operator) && scalar(item.value);
};

/** Keep invalid local edits recoverable inside Selection, before parent capability checks. */
export const selectionDraftValid = policy => {
  if (
    !Array.isArray(policy?.sources) ||
    !policy.sources.length ||
    policy.sources.length > 5
  )
    return false;
  const budget = { total: 0, predicates: 0 };
  const ids = new Set();
  return policy.sources.every(source => {
    if (!text(source.id, 100) || ids.has(source.id)) return false;
    ids.add(source.id);
    if (source.query != null && !text(source.query, 500)) return false;
    let leaves = 0;
    const groupValid = (group, depth = 1) => {
      if (
        depth > 3 ||
        !['all', 'any'].includes(group?.match) ||
        !Array.isArray(group.items) ||
        group.items.length > 10 ||
        (depth > 1 && group.items.length < 2)
      )
        return false;
      return group.items.every(item => {
        if (Array.isArray(item?.items)) return groupValid(item, depth + 1);
        leaves += 1;
        budget.total += 1;
        if (leaves > 10 || budget.total > 50 || !leafValid(item)) return false;
        if (
          item.include_descendants !== undefined &&
          (item.field !== 'category_ids' ||
            typeof item.include_descendants !== 'boolean')
        )
          return false;
        budget.predicates +=
          (['contains_all', ...textOperators].includes(item.operator)
            ? item.value.length
            : 1) + (legacy(item) ? 1 : 3);
        return budget.predicates <= 256;
      });
    };
    return (
      groupValid(source.conditions) && (leaves > 0 || source.query != null)
    );
  });
};
